import { useEffect, useMemo, useState, useCallback } from 'react';

// const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const LABELS = {
  trendyol: 'Trendyol',
  hepsiburada: 'Hepsiburada',
  n11: 'N11',
  ciceksepeti: 'Çiçeksepeti',
  efidanci: 'Efidancı'
};

function normalizeStore(store) {
  return {
    id: store.id,
    marketplace: store.marketplace,
    storeName: store.store_name || store.storeName,
    supplierId: store.supplier_id || store.supplierId || '',
    apiKey: store.api_key || store.apiKey || '',
    apiSecret: store.api_secret || store.apiSecret || '',
    active: Boolean(store.is_active ?? store.active)
  };
}

export function useMarketplaceConfig() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStores = useCallback(async () => {
    try {
      const response = await fetch('/api/stores', {
        headers: { Accept: 'application/json' },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setStores(data.stores.map(normalizeStore));
        }
      }
    } catch (err) {
      console.error('Failed to fetch stores:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  async function addStore(entry) {
    try {
      const payload = {
        marketplace: entry.marketplace,
        store_name: entry.storeName,
        api_key: entry.apiKey,
        api_secret: entry.apiSecret,
        supplier_id: entry.supplierId
      };
      const response = await fetch('/api/stores', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });
      if (response.ok) {
        await fetchStores();
      }
    } catch (err) {
      console.error('Failed to add store:', err);
    }
  }

  async function updateStore(id, updates) {
    try {
      const payload = {
        store_name: updates.storeName,
        api_key: updates.apiKey,
        api_secret: updates.apiSecret,
        supplier_id: updates.supplierId,
        is_active: updates.active
      };
      const response = await fetch(`/api/stores/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });
      if (response.ok) {
        await fetchStores();
      }
    } catch (err) {
      console.error('Failed to update store:', err);
    }
  }

  async function removeStore(id) {
    try {
      const response = await fetch(`/api/stores/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        await fetchStores();
      }
    } catch (err) {
      console.error('Failed to remove store:', err);
    }
  }

  const groupedConfig = useMemo(() => {
    const groups = {
      trendyol: { label: LABELS.trendyol, stores: [] },
      hepsiburada: { label: LABELS.hepsiburada, stores: [] },
      n11: { label: LABELS.n11, stores: [] },
      ciceksepeti: { label: LABELS.ciceksepeti, stores: [] },
      efidanci: { label: LABELS.efidanci, stores: [] }
    };

    stores.forEach((store) => {
      if (groups[store.marketplace]) {
        groups[store.marketplace].stores.push(store);
      }
    });

    return groups;
  }, [stores]);

  const activeStoreMap = useMemo(() => {
    const map = {};
    stores.forEach((store) => {
      if (store.active) {
        if (!map[store.marketplace]) {
          map[store.marketplace] = new Set();
        }
        map[store.marketplace].add(store.storeName);
      }
    });
    return map;
  }, [stores]);

  return {
    stores,
    loading,
    error,
    groupedConfig,
    activeStoreMap,
    addStore,
    updateStore,
    removeStore
  };
}
